import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/lib/cart";
import { useAuth, clearSession } from "@/lib/auth";
import { NAV } from "@/constants/testIds";
import SearchAutocomplete from "@/components/SearchAutocomplete";
import Logo from "@/components/Logo";
import { ShoppingBag, UserCircle, List, X, SignOut } from "@phosphor-icons/react";

const linkBase =
  "block w-full text-left text-2xl font-display font-black tracking-tighter transition-colors hover:text-[#FF5A1F]";

const navItems = [
  { name: "Home", to: "/", testId: NAV.homeLink, end: true },
  { name: "About", to: "/about", testId: NAV.about },
  { name: "Shop", to: "/shop", testId: NAV.shop },
  { name: "Flavour Finder", to: "/quiz", testId: "nav-quiz" },
  { name: "Contact", to: "/contact", testId: NAV.contact },
];

export default function Navbar() {
  const { count } = useCart();
  const { user, isAdmin } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  // Lock body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-2xl">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-4 sm:px-6 lg:px-10">
          <Link
            to="/"
            data-testid={NAV.home}
            className="flex items-center gap-2"
          >
            <Logo size={34} />
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:block">
              <SearchAutocomplete />
            </div>
            <Link
              to="/cart"
              data-testid={NAV.cart}
              className="relative inline-flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 sm:px-4 py-2 text-xs font-label hover:border-[#00FFC2] hover:text-[#00FFC2] transition-colors"
            >
              <ShoppingBag size={16} weight="bold" />
              <span className="hidden sm:inline">Cart</span>
              <motion.span
                key={count}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                data-testid={NAV.cartCount}
                className="ml-1 rounded-full bg-[#FF5A1F] px-2 py-[1px] text-[10px] font-bold text-black"
              >
                {count}
              </motion.span>
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="nav-hamburger-btn"
              aria-label="Open menu"
              className="inline-flex h-10 w-10 items-center justify-center border border-white/15 bg-white/5 hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors"
            >
              <List size={20} weight="bold" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setOpen(false)}
              data-testid="nav-drawer-backdrop"
              className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
            />

            {/* Drawer */}
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              data-testid="nav-drawer"
              className="fixed inset-y-0 right-0 z-[70] w-full max-w-[380px] border-l border-white/10 bg-[#0a0a0a] shadow-[-20px_0_60px_rgba(0,0,0,0.6)] flex flex-col"
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                <span className="font-label text-[10px] text-white/40">/ MENU</span>
                <button
                  onClick={() => setOpen(false)}
                  data-testid="nav-drawer-close"
                  aria-label="Close menu"
                  className="inline-flex h-9 w-9 items-center justify-center border border-white/15 hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>

              {/* Mobile-only search */}
              <div className="sm:hidden border-b border-white/10 p-4">
                <SearchAutocomplete />
              </div>

              {/* Nav links */}
              <nav className="flex-1 overflow-y-auto px-6 py-8">
                <ul className="space-y-5">
                  {navItems.map((item, i) => (
                    <motion.li
                      key={item.name}
                      initial={{ x: 30, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.05 + i * 0.05 }}
                    >
                      <NavLink
                        to={item.to}
                        end={item.end}
                        data-testid={item.testId}
                        className={({ isActive }) =>
                          `${linkBase} ${isActive ? "text-[#FF5A1F]" : "text-white"}`
                        }
                      >
                        {item.name}
                        <span className="ml-2 text-[#00FFC2]">.</span>
                      </NavLink>
                    </motion.li>
                  ))}

                  {isAdmin && (
                    <motion.li
                      initial={{ x: 30, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.35 }}
                    >
                      <NavLink
                        to="/admin"
                        data-testid={NAV.admin}
                        className={({ isActive }) =>
                          `${linkBase} ${isActive ? "text-[#00FFC2]" : "text-[#FFC900]"}`
                        }
                      >
                        Admin
                        <span className="ml-2 text-[#FF5A1F]">.</span>
                      </NavLink>
                    </motion.li>
                  )}
                </ul>

                <div className="mt-10 space-y-3 border-t border-white/10 pt-6">
                  {user ? (
                    <>
                      <Link
                        to="/account"
                        data-testid={NAV.account}
                        className="flex items-center gap-3 border border-white/15 px-4 py-3 text-xs font-label text-white/80 hover:border-[#FF5A1F] hover:text-white transition-colors"
                      >
                        <UserCircle size={18} weight="duotone" />
                        <div>
                          <div>{(user.email || "").split("@")[0]}</div>
                          <div className="text-[10px] text-white/40">My Profile</div>
                        </div>
                      </Link>
                      <button
                        data-testid={NAV.logoutBtn}
                        onClick={() => {
                          clearSession();
                          setOpen(false);
                          nav("/");
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 border border-white/15 px-4 py-3 text-xs font-label text-white/60 hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors"
                      >
                        <SignOut size={14} weight="bold" /> Sign out
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/account"
                      data-testid={NAV.account}
                      className="flex items-center justify-center gap-2 bg-[#FF5A1F] px-4 py-3 text-xs font-label text-black hover:-translate-y-0.5 transition-transform"
                    >
                      <UserCircle size={16} weight="bold" />
                      Sign in / Register
                    </Link>
                  )}
                </div>
              </nav>

              {/* Drawer footer */}
              <div className="border-t border-white/10 px-6 py-4 text-[10px] font-label text-white/30">
                © {new Date().getFullYear()} VAPE.ESSENCE — 18+
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
